import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { LayoutTemplate, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const EMPTY_ROW = { title: "", description: "", estimated_hours: "", priority: "medium" };

export default function TaskTemplates() {
  const qc = useQueryClient();
  const { can } = useApp();
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({ name: "", description: "" });
  const [rowForms, setRowForms] = useState({});

  const { data: templates, isLoading } = useQuery({ queryKey: ["task-templates"], queryFn: () => get("/pm/task-templates") });

  const create = useMutation({
    mutationFn: (body) => post("/pm/task-templates", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["task-templates"] }); toast.success("Template created"); setAdding(false); setForm({ name: "", description: "" }); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }) => patch(`/pm/task-templates/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["task-templates"] }); toast.success("Template updated"); },
  });
  const remove = useMutation({
    mutationFn: (id) => del(`/pm/task-templates/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["task-templates"] }); toast.success("Template deleted"); },
  });

  const addRow = (tmpl) => {
    const row = rowForms[tmpl.id] || EMPTY_ROW;
    if (!row.title) { toast.error("Task title is required"); return; }
    const tasks = [...(tmpl.tasks || []), { ...row, estimated_hours: Number(row.estimated_hours) || 0 }];
    update.mutate({ id: tmpl.id, body: { tasks } });
    setRowForms((f) => ({ ...f, [tmpl.id]: EMPTY_ROW }));
  };
  const removeRow = (tmpl, idx) => {
    const tasks = tmpl.tasks.filter((_, i) => i !== idx);
    update.mutate({ id: tmpl.id, body: { tasks } });
  };

  return (
    <div>
      <PageHeader title="Task Templates" subtitle="Company-wide standard task breakdowns, applied during Task Planning.">
        {can("pm", "edit") && <Button data-testid="add-template-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />New Template</Button>}
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <Label>Name</Label>
              <Input data-testid="template-name" className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <Label className="mt-3 block">Description</Label>
              <Textarea data-testid="template-description" className="mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" data-testid="template-submit" onClick={() => {
                  if (!form.name) { toast.error("Name is required"); return; }
                  create.mutate(form);
                }}>Create</Button>
              </div>
            </div>
          )}
          {!templates?.length ? <EmptyState icon={LayoutTemplate} title="No task templates yet" /> : (
            <div className="divide-y divide-border">
              {templates.map((tmpl) => {
                const open = expanded[tmpl.id];
                const row = rowForms[tmpl.id] || EMPTY_ROW;
                return (
                  <div key={tmpl.id}>
                    <div className="flex items-center gap-3 px-4 py-3 text-sm">
                      <button onClick={() => setExpanded((s) => ({ ...s, [tmpl.id]: !s[tmpl.id] }))} className="text-muted-foreground hover:text-foreground">
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <div className="flex-1">
                        <div className="font-medium">{tmpl.name}</div>
                        <div className="text-xs text-muted-foreground">{tmpl.description}</div>
                      </div>
                      <StatusBadge family="slate" label={`${(tmpl.tasks || []).length} tasks`} />
                      {can("pm", "delete") && (
                        <ConfirmDelete testid={`del-template-${tmpl.id}`} title={`Delete ${tmpl.name}?`}
                          trigger={<button className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                          onConfirm={() => remove.mutate(tmpl.id)} />
                      )}
                    </div>
                    {open && (
                      <div className="bg-muted/20 px-4 py-3 pl-11">
                        <div className="space-y-1.5">
                          {(tmpl.tasks || []).map((t, i) => (
                            <div key={i} className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs">
                              <span className="flex-1 font-medium">{t.title}</span>
                              <span className="text-muted-foreground">{t.estimated_hours}h</span>
                              <StatusBadge status={t.priority} />
                              {can("pm", "edit") && <button onClick={() => removeRow(tmpl, i)} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                            </div>
                          ))}
                        </div>
                        {can("pm", "edit") && (
                          <div className="mt-2 flex items-end gap-2">
                            <Input placeholder="Task title" className="h-8 flex-1 text-xs" value={row.title} onChange={(e) => setRowForms((f) => ({ ...f, [tmpl.id]: { ...row, title: e.target.value } }))} />
                            <Input placeholder="Hours" type="number" className="h-8 w-20 text-xs" value={row.estimated_hours} onChange={(e) => setRowForms((f) => ({ ...f, [tmpl.id]: { ...row, estimated_hours: e.target.value } }))} />
                            <Button size="sm" className="h-8" onClick={() => addRow(tmpl)}>Add Row</Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
